import * as React from "react";
import { useQuery } from "convex/react";
import {
  Users,
  DollarSign,
  Package,
  TrendingUp,
  ChevronDown,
  ChevronRight,
  BarChart3,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { api } from "@/lib/convexApi";

interface SalesRepPerformanceProps {
  authUserId?: string | null;
}

const dateRangeOptions = [
  { value: "30d", label: "30 days" },
  { value: "90d", label: "90 days" },
  { value: "6mo", label: "6 months" },
  { value: "1y", label: "1 year" },
  { value: "all", label: "All time" },
] as const;

function fmtCurrency(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

function fmtNumber(num: number): string {
  return new Intl.NumberFormat("en-US").format(num);
}

function fmtPercent(value: number | null | undefined): string {
  if (value === null || value === undefined) return "-";
  const sign = value > 0 ? "+" : value < 0 ? "-" : "";
  const absValue = Math.abs(value);
  return `${sign}${absValue.toFixed(1)}%`;
}

export function SalesRepPerformance({ authUserId }: SalesRepPerformanceProps) {
  const [dateRange, setDateRange] = React.useState<string>("90d");
  const [deliveredOnly] = React.useState(false);
  const [expandedReps, setExpandedReps] = React.useState<Set<string>>(
    () => {
      try {
        const stored = localStorage.getItem("sales-rep-expanded");
        return stored ? new Set(JSON.parse(stored)) : new Set();
      } catch {
        return new Set();
      }
    },
  );

  const salesRepData = useQuery(
    api.analytics.getSalesBySalesRep,
    authUserId ? { dateRange, deliveredOnly } : "skip",
  );

  const isLoading = salesRepData === undefined;

  const toggleExpanded = React.useCallback((salesRepId: string) => {
    setExpandedReps((prev) => {
      const next = new Set(prev);
      if (next.has(salesRepId)) {
        next.delete(salesRepId);
      } else {
        next.add(salesRepId);
      }
      try {
        localStorage.setItem("sales-rep-expanded", JSON.stringify([...next]));
      } catch { /* ignore */ }
      return next;
    });
  }, []);

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="space-y-1">
            <CardTitle className="flex items-center gap-2 text-sm font-semibold">
              <BarChart3 className="size-4" />
              Sales Rep Performance
            </CardTitle>
            <CardDescription className="text-xs">
              Revenue and client breakdown by sales representative
            </CardDescription>
          </div>
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-[140px] text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {dateRangeOptions.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {isLoading ? (
          <div className="space-y-4">
            <div className="grid gap-4 md:grid-cols-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-20" />
              ))}
            </div>
            <Skeleton className="h-64" />
          </div>
        ) : salesRepData ? (
          <>
            {/* Summary Cards */}
            <div className="grid gap-4 md:grid-cols-4">
              <div className="rounded-lg border bg-muted/30 p-4">
                <div className="flex items-center gap-2">
                  <Users className="size-4 text-muted-foreground" />
                  <span className="text-xs font-medium text-muted-foreground">Sales Reps</span>
                </div>
                <p className="mt-2 text-2xl font-bold">{salesRepData.totals.salesRepCount}</p>
                <p className="text-xs text-muted-foreground">
                  {salesRepData.totals.clientCount} total clients
                </p>
              </div>
              <div className="rounded-lg border bg-muted/30 p-4">
                <div className="flex items-center gap-2">
                  <DollarSign className="size-4 text-muted-foreground" />
                  <span className="text-xs font-medium text-muted-foreground">Total Revenue</span>
                </div>
                <p className="mt-2 text-2xl font-bold">{fmtCurrency(salesRepData.totals.totalRevenue)}</p>
                <p className="text-xs text-muted-foreground">{salesRepData.totals.orderCount} orders</p>
              </div>
              <div className="rounded-lg border bg-muted/30 p-4">
                <div className="flex items-center gap-2">
                  <Package className="size-4 text-muted-foreground" />
                  <span className="text-xs font-medium text-muted-foreground">Total Bags</span>
                </div>
                <p className="mt-2 text-2xl font-bold">{fmtNumber(salesRepData.totals.totalBags)}</p>
                <p className="text-xs text-muted-foreground">
                  {fmtNumber(salesRepData.totals.totalPounds)} lbs
                </p>
              </div>
              <div className="rounded-lg border bg-muted/30 p-4">
                <div className="flex items-center gap-2">
                  <TrendingUp className="size-4 text-muted-foreground" />
                  <span className="text-xs font-medium text-muted-foreground">Contract vs Spot</span>
                </div>
                <p className="mt-2 text-2xl font-bold">
                  {salesRepData.totals.totalRevenue > 0
                    ? Math.round(
                        (salesRepData.totals.contractRevenue / salesRepData.totals.totalRevenue) * 100,
                      )
                    : 0}%
                </p>
                <p className="text-xs text-muted-foreground">
                  {fmtCurrency(salesRepData.totals.contractRevenue)} contract
                </p>
              </div>
            </div>

            {/* Table */}
            {salesRepData.data.length === 0 ? (
              <div className="flex h-32 items-center justify-center text-sm text-muted-foreground">
                No sales data found for the selected period
              </div>
            ) : (
              <div className="overflow-x-auto rounded-lg border">
                <Table className="text-xs">
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead className="w-8" />
                      <TableHead>Sales Rep</TableHead>
                      <TableHead className="text-right">Revenue</TableHead>
                      <TableHead className="text-right">Bags</TableHead>
                      <TableHead className="text-right">Clients</TableHead>
                      <TableHead className="text-right">Orders</TableHead>
                      <TableHead className="text-right">Avg Order</TableHead>
                      <TableHead className="text-center">Contract %</TableHead>
                      <TableHead className="text-right">Rev 30d</TableHead>
                      <TableHead className="text-right">Rev Prev 30d</TableHead>
                      <TableHead className="text-right">Rev 30d %</TableHead>
                      <TableHead className="text-right">Rev Avg/Mo</TableHead>
                      <TableHead className="text-right">Bags 30d</TableHead>
                      <TableHead className="text-right">Bags Prev 30d</TableHead>
                      <TableHead className="text-right">Bags 30d %</TableHead>
                      <TableHead className="text-right">Bags Avg/Mo</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {salesRepData.data.map((rep: any) => {
                      const isExpanded = expandedReps.has(rep.salesRepId);
                      return (
                        <React.Fragment key={rep.salesRepId}>
                          <TableRow
                            className="cursor-pointer hover:bg-muted/30"
                            onClick={() => toggleExpanded(rep.salesRepId)}
                          >
                            <TableCell className="w-8">
                              <Button variant="ghost" size="sm" className="size-6 p-0">
                                {isExpanded ? (
                                  <ChevronDown className="size-3" />
                                ) : (
                                  <ChevronRight className="size-3" />
                                )}
                              </Button>
                            </TableCell>
                            <TableCell className="font-medium">
                              {rep.salesRepId === "unassigned" ? (
                                <span className="italic text-muted-foreground">Unassigned</span>
                              ) : (
                                rep.salesRepName || rep.salesRepId
                              )}
                            </TableCell>
                            <TableCell className="text-right font-semibold">
                              {fmtCurrency(rep.metrics.totalRevenue)}
                            </TableCell>
                            <TableCell className="text-right">{fmtNumber(rep.metrics.totalBags)}</TableCell>
                            <TableCell className="text-right">{rep.metrics.clientCount}</TableCell>
                            <TableCell className="text-right">{rep.metrics.orderCount}</TableCell>
                            <TableCell className="text-right">{fmtCurrency(rep.metrics.avgOrderValue)}</TableCell>
                            <TableCell className="text-center">
                              <Badge
                                variant={rep.breakdown.contractPercent > 50 ? "default" : "secondary"}
                                className="text-xs"
                              >
                                {rep.breakdown.contractPercent}%
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right font-semibold">
                              {fmtCurrency(rep.metrics.last30Revenue)}
                            </TableCell>
                            <TableCell className="text-right">
                              {fmtCurrency(rep.metrics.previous30Revenue)}
                            </TableCell>
                            <TableCell
                              className={`text-right tabular-nums ${
                                rep.metrics.revenueMoMPercent === null
                                  ? "text-muted-foreground"
                                  : rep.metrics.revenueMoMPercent >= 0
                                    ? "text-emerald-600"
                                    : "text-rose-600"
                              }`}
                            >
                              {fmtPercent(rep.metrics.revenueMoMPercent)}
                            </TableCell>
                            <TableCell className="text-right tabular-nums">
                              {fmtCurrency(rep.metrics.avgMonthlyRevenue)}
                            </TableCell>
                            <TableCell className="text-right">{fmtNumber(rep.metrics.last30Bags)}</TableCell>
                            <TableCell className="text-right">{fmtNumber(rep.metrics.previous30Bags)}</TableCell>
                            <TableCell
                              className={`text-right tabular-nums ${
                                rep.metrics.bagsMoMPercent === null
                                  ? "text-muted-foreground"
                                  : rep.metrics.bagsMoMPercent >= 0
                                    ? "text-emerald-600"
                                    : "text-rose-600"
                              }`}
                            >
                              {fmtPercent(rep.metrics.bagsMoMPercent)}
                            </TableCell>
                            <TableCell className="text-right tabular-nums">
                              {fmtNumber(rep.metrics.avgMonthlyBags)}
                            </TableCell>
                          </TableRow>

                          {/* Expanded Row */}
                          {isExpanded && (
                            <TableRow className="bg-muted/20">
                              <TableCell colSpan={16} className="p-0">
                                {rep.topClients.length > 0 ? (
                                  <div className="px-8 py-4">
                                    <p className="mb-3 text-xs font-medium text-muted-foreground">
                                      Top {rep.topClients.length} Clients by Revenue
                                    </p>
                                    <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
                                      {rep.topClients.map((client: any, index: number) => (
                                        <div
                                          key={client.clientName}
                                          className="flex items-center justify-between rounded-md border bg-background p-3"
                                        >
                                          <div className="min-w-0 flex-1">
                                            <p className="truncate text-xs font-medium" title={client.clientName}>
                                              {index + 1}. {client.clientName}
                                            </p>
                                            {client.company && client.company !== client.clientName && (
                                              <p className="truncate text-xs text-muted-foreground">
                                                {client.company}
                                              </p>
                                            )}
                                            <p className="text-xs text-muted-foreground">
                                              {client.bags} bags &middot; {client.orderCount} orders
                                              {client.tier && <> &middot; Tier {client.tier}</>}
                                            </p>
                                          </div>
                                          <div className="ml-2 text-right">
                                            <p className="text-xs font-semibold">{fmtCurrency(client.revenue)}</p>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                    <div className="mt-4 flex flex-wrap gap-4 text-xs text-muted-foreground">
                                      <span>
                                        <span className="font-medium text-foreground">Contract:</span>{" "}
                                        {fmtCurrency(rep.breakdown.contractRevenue)} ({rep.breakdown.contractBags} bags)
                                      </span>
                                      <span>
                                        <span className="font-medium text-foreground">Spot:</span>{" "}
                                        {fmtCurrency(rep.breakdown.spotRevenue)} ({rep.breakdown.spotBags} bags)
                                      </span>
                                      <span>
                                        <span className="font-medium text-foreground">Avg $/lb:</span>{" "}
                                        ${rep.metrics.avgPricePerLb.toFixed(2)}
                                      </span>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="px-8 py-4 text-xs italic text-muted-foreground">
                                    No client data available
                                  </div>
                                )}
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </>
        ) : (
          <div className="flex h-32 items-center justify-center text-sm text-muted-foreground">
            Unable to load sales rep data
          </div>
        )}
      </CardContent>
    </Card>
  );
}
